import {
  Button,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { fetchBranchMembers } from "../../apis/branch.api";
import {
  depositCashLog,
  fetchCashLogGroupedByUser,
  fetchCashLogSummaryByBranch,
  uploadImage,
} from "../../apis/misc.api";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";

interface CashDepositProps {
  onSuccess?: () => void;
}

export function CashDeposit({ onSuccess }: CashDepositProps = {}) {
  const { hasPermission } = usePermissions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form] = Form.useForm();
  const [branches, setBranches] = useState<any[]>([]);
  const [deposited_cash_by_user, setDepositedCashByUser] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [selectedBranchMemebers, setSelectedBranchMembers] = useState<any[]>(
    []
  );
  const [selectedMember, setSelectedMember] = useState<any | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [maxDepositAmount, setMaxDepositAmount] = useState<number>(0);
  const [preSelectedMemberUserID, setPreSelectedMemberUserID] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (selectedMember) {
      const userCashLog = deposited_cash_by_user?.find(
        (log: any) => log?.user_id === selectedMember?.user?.id
      );
      if (userCashLog) {
        form.setFieldValue(
          "amount_in_hand",
          userCashLog?.collection - userCashLog?.deposit
        );
        setMaxDepositAmount(userCashLog?.collection - userCashLog?.deposit);
      } else {
        form.setFieldValue("amount_in_hand", 0);
        setMaxDepositAmount(0);
      }
    } else {
      form.setFieldValue("amount_in_hand", 0);
      form.setFieldValue("deposit_amount", 0);
      setMaxDepositAmount(0);
    }
  }, [selectedMember, deposited_cash_by_user]);

  useEffect(() => {
    if (selectedBranch) {
      fetchBranchMembers({
        branch: selectedBranch?.organisation_branch_id,
      }).then((members) => {
        setSelectedBranchMembers(members ?? []);
        if (preSelectedMemberUserID) {
          const selectedMember = members?.find(
            (member: any) => member?.user?.id === preSelectedMemberUserID
          );
          if (selectedMember) {
            setSelectedMember(selectedMember);
            form.setFieldValue("collector", selectedMember?.id);
          }
        }
      });

      // form.setFieldValue(
      //   "amount_in_hand",
      //   selectedBranch?.collection - selectedBranch?.deposit
      // );
    } else {
      setSelectedBranchMembers([]);
      form.resetFields(["collector", "amount_in_hand"]);
      setAttachmentUrls([]);
      setMaxDepositAmount(0);
    }
  }, [selectedBranch]);

  const getBranches = async () => {
    const summaryByBranch = await fetchCashLogSummaryByBranch();
    setBranches(summaryByBranch);
  };

  const tableColumns = [
    {
      key: "s_no",
      title: "S.No.",
      render: (_: any, __: any, index: number) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      key: "organisation_branch_name",
      title: "Branch Name",
      dataIndex: "organisation_branch_name",
    },
    {
      key: "cash_collected",
      title: "Cash Collected",
      dataIndex: "collection",
    },
    {
      key: "cash_deposited",
      title: "Cash Deposited",
      dataIndex: "deposit",
    },
    {
      key: "cash_remaining",
      title: "Cash Remaining",
      render: (record: any) => {
        return <div>{record?.collection - record?.deposit}</div>;
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (record: any) => {
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                // Handle view action
                setSelectedBranch(record);
              }}
            >
              Mark Deposit
            </Button>
          </Space>
        );
      },
    },
  ];

  const tableColumnsDepositedCashByMember = [
    {
      key: "s_no",
      title: "S.No.",
      render: (_: any, __: any, index: number) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      key: "f_name",
      title: "Member Name",
      dataIndex: "f_name",
    },
    {
      key: "cash_collected",
      title: "Cash Collected",
      dataIndex: "collection",
    },
    {
      key: "cash_deposited",
      title: "Cash Deposited",
      dataIndex: "deposit",
    },
    {
      key: "cash_remaining",
      title: "Cash Remaining",
      render: (record: any) => {
        return <div>{record?.collection - record?.deposit}</div>;
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (record: any) => {
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                // Handle view action
                setPreSelectedMemberUserID(record?.user_id);
                setSelectedBranch(record);
              }}
            >
              Mark Deposit
            </Button>
          </Space>
        );
      },
    },
  ];

  const getDepositedCashbyMember = async () => {
    const summaryByUser = await fetchCashLogGroupedByUser();
    setDepositedCashByUser(summaryByUser);
  };

  useEffect(() => {
    getBranches();
    getDepositedCashbyMember();
  }, []);

  const onFinish = async (values: any) => {
    if (!hasPermission("finance.create")) {
      message.error("You don't have permission to mark a deposit");
      return;
    }
    if (attachmentUrls.length === 0) {
      message.error("Please upload at least one attachment");
      return;
    }
    const collectorUser = selectedBranchMemebers.find(
      (member) => member.id === values?.collector
    );
    const data = {
      ...values,
      date_of_deposit: values?.date_of_deposit?.format("YYYY-MM-DD"),
      organisation_branch: selectedBranch?.organisation_branch_id,
      documents: attachmentUrls,
      payment_amount: 0,
      uuid: collectorUser?.user?.id,
      log_type: "deposit",
    };

    const response = await depositCashLog(data);
    if (response) {
      message.success("Deposit marked successfully");
      setSelectedBranch(null);
      setAttachmentUrls([]);
      form.resetFields();

      await Promise.all([getBranches(), getDepositedCashbyMember()]);

      if (onSuccess) {
        onSuccess();
      }
    } else {
      message.error("Failed to mark deposit");
    }
  };

  return (
    <div>
      <Table
        columns={tableColumns}
        dataSource={branches}
        pagination={false}
        rowKey={(record) => record.id}
        className="th-font-size-16"
        scroll={{ x: 1000 }}
      />
      <Table
        columns={tableColumnsDepositedCashByMember}
        dataSource={deposited_cash_by_user}
        pagination={false}
        className="th-font-size-16 mt-4"
        scroll={{ x: 1000 }}
      />
      <Drawer
        open={selectedBranch !== null}
        onClose={() => setSelectedBranch(null)}
        title="Mark Deposit"
        width={400}
        destroyOnClose={false}
      >
        <Form layout="vertical" onFinish={onFinish} form={form}>
          <Form.Item
            label="Collector"
            name="collector"
            rules={[
              {
                required: true,
                message: "Please select a collector",
              },
            ]}
          >
            <Select
              disabled
              showSearch
              placeholder="Select Collector"
              filterOption={(input, option) => {
                return (
                  option?.label?.toLowerCase().includes(input.toLowerCase()) ??
                  false
                );
              }}
              options={selectedBranchMemebers
                .filter((member) => {
                  return [1, 2].includes(member?.user_permission); // Admin, Manager, Sales
                })
                .map((member) => ({
                  label: member.user?.f_name,
                  value: member.id,
                }))}
              onChange={(value) => {
                const selectedMember = selectedBranchMemebers.find(
                  (member) => member.id === value
                );
                setSelectedMember(selectedMember);
              }}
              className="w-100"
            />
          </Form.Item>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <Form.Item label="Amount In Hand" name="amount_in_hand">
              <InputNumber
                className="w-100"
                placeholder="Enter Amount"
                min={0}
                readOnly
              />
            </Form.Item>
            <Form.Item
              label="Amount Deposited"
              name="deposit_amount"
              rules={[
                {
                  required: true,
                  message: "Please enter the amount deposited",
                },
              ]}
            >
              <InputNumber
                className="w-100"
                placeholder="Enter Amount"
                min={0}
                max={maxDepositAmount}
              />
            </Form.Item>
          </div>
          <Form.Item
            label="Date of Deposit"
            name="date_of_deposit"
            getValueProps={(value) => {
              return {
                value: dayjs(value),
              };
            }}
            rules={[
              {
                required: true,
                message: "Please select a date",
              },
            ]}
          >
            <DatePicker className="w-100" placeholder="Select Date" />
          </Form.Item>
          <div className="mt-4">
            <Typography.Title level={5}>Attachments</Typography.Title>
            {attachmentUrls?.map((url, index) => {
              return (
                <div
                  key={index}
                  style={{
                    width: "100%",
                    marginBottom: "0.5rem",
                  }}
                  className="d-flex justify-content-between align-items-center"
                >
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    File {index + 1}
                  </a>
                </div>
              );
            })}
            <input
              type="file"
              multiple
              style={{
                width: 1,
                height: 1,
                opacity: 0,
                position: "absolute",
                zIndex: -1,
                pointerEvents: "none",
              }}
              onChange={async (e) => {
                const files = e.target.files;
                if (files) {
                  message.loading({
                    content: "Uploading files...",
                    key: "uploading",
                  });
                  const urls = await Promise.all(
                    Array.from(files).map(async (file) => {
                      const formData = new FormData();
                      formData.append("file", file);
                      return uploadImage(formData);
                    })
                  );
                  setAttachmentUrls((prev) => {
                    return [...prev, ...urls];
                  });

                  message.success({
                    content: "Files uploaded successfully",
                    key: "uploading",
                    duration: 2,
                  });
                }
              }}
              ref={fileInputRef}
            />
            <Button
              type="default"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
              style={{ marginTop: "1rem" }}
            >
              Upload Attachments
            </Button>
          </div>
          <Form.Item className="d-flex justify-content-end">
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
